"use client";
import type React from "react";
import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { X, Maximize2, Minimize2, Clock, Loader2 } from "lucide-react";
import { ChatContainer, ChatMessages } from "@/components/ui/chat";
import { MessageList } from "@/components/ui/message-list";
import { MessageInput } from "@/components/ui/message-input";
import { useCloudMigrationChat } from "@/hooks/useCloudMigrationChat";
import { useValidation } from "@/hooks/useValidation";
import { useToast } from "@/components/ui/use-toast";
import {
  FEATURE_FLAGS,
  useFeatureFlagWithDefault,
} from "@/hooks/useFeatureFlag";
import ConversationList from "../Chat/ConversationList";

interface ChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
  initialMessage?: string;
  initialFiles?: File[] | undefined;
  projectId?: number | null;
}

export function ChatPanel({
  isOpen,
  onClose,
  initialMessage,
  initialFiles,
  projectId,
}: ChatPanelProps) {
  const { isEnabled } = useFeatureFlagWithDefault(
    FEATURE_FLAGS.ENABLE_MIGRATION_AGENT,
    true
  );

  const {
    messages,
    isGenerating,
    error,
    sendMessage,
    clearMessages,
    updateMessages,
    conversationId,
    loadConversation,
    loadOlderMessages,
    hasOlder,
  } = useCloudMigrationChat();

  // User quota data
  const { data: validationData } = useValidation();

  const quotaRemaining = (() => {
    if (!validationData) return Infinity;
    if (projectId) {
      return (
        (validationData.project_file_limit ?? Infinity) -
        (validationData.project_files_used ?? 0)
      );
    }
    return (
      (validationData.global_file_limit ?? Infinity) -
      (validationData.global_files_used ?? 0)
    );
  })();

  // Toast for displaying errors (e.g., file upload failures)
  const { toast } = useToast();

  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: error,
        variant: "destructive",
      });
    }
  }, [error, toast]);

  // Listen for global event from history sidebar
  useEffect(() => {
    const handler = (e: any) => {
      const id = e.detail?.id as string | undefined;
      if (id) loadConversation(id);
    };
    window.addEventListener("caden-select-conversation", handler as any);
    return () =>
      window.removeEventListener("caden-select-conversation", handler as any);
  }, [loadConversation]);

  const topSentinelRef = useRef<HTMLDivElement>(null);

  // Observe top sentinel to load older messages
  useEffect(() => {
    if (!topSentinelRef.current) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        loadOlderMessages();
      }
    });
    observer.observe(topSentinelRef.current);
    return () => observer.disconnect();
  }, [loadOlderMessages]);

  const [isExpanded, setIsExpanded] = useState(false);

  const [input, setInput] = useState("");
  const [files, setFiles] = useState<File[] | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [showHistory, setShowHistory] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  useEffect(() => {
    if (
      isOpen &&
      (initialMessage || (initialFiles && initialFiles.length > 0)) &&
      messages.length === 0
    ) {
      sendMessage(initialMessage ?? "", projectId, initialFiles || undefined);
    }
  }, [
    isOpen,
    initialMessage,
    initialFiles,
    projectId,
    messages.length,
    sendMessage,
  ]);

  // Clear messages when panel closes
  useEffect(() => {
    if (!isOpen) {
      clearMessages();
      setInput("");
      setFiles(null);
    }
  }, [isOpen, clearMessages]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (input.trim() && !isGenerating) {
      sendMessage(input, projectId, files || undefined);
      setInput("");
      setFiles(null);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  };

  const handleLoadConversation = async (id: string) => {
    setIsLoadingHistory(true);
    try {
      await loadConversation(id);
      setShowHistory(false);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  if (!isEnabled) return null;

  // Compute dynamic classes for slide-in (open) and slide-out (closed)
  const panelClasses = `fixed inset-y-0 right-0 ${
    isExpanded ? "w-[50vw] max-w-[720px]" : "w-96"
  } bg-white border-l border-[#DEDEDE] shadow-2xl z-50 flex flex-col transform transition-transform ease-in-out ${
    isOpen ? "translate-x-0" : "translate-x-full pointer-events-none"
  }`;

  return (
    <>
      <div className={panelClasses} style={{ transitionDuration: "400ms" }}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-white">
          <h2 className="font-medium text-gray-900">Caden</h2>
          <div className="flex items-center gap-1">
            {/* History button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowHistory(!showHistory)}
              className="h-8 w-8 p-0 hover:bg-gray-100"
              aria-label={showHistory ? "Close history" : "Open history"}
            >
              <Clock className="h-4 w-4" />
            </Button>
            {/* Expand / Collapse Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded((prev) => !prev)}
              className="h-8 w-8 p-0 hover:bg-gray-100"
              aria-label={
                isExpanded ? "Collapse chat panel" : "Expand chat panel"
              }
            >
              {isExpanded ? (
                <Minimize2 className="h-4 w-4" />
              ) : (
                <Maximize2 className="h-4 w-4" />
              )}
            </Button>

            {/* Close Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0 hover:bg-gray-100"
              aria-label="Close chat panel"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Messages Container or History */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {isLoadingHistory ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : showHistory ? (
            <div className="flex-1 overflow-auto">
              <ConversationList
                projectId={projectId ?? null}
                onSelect={handleLoadConversation}
              />
            </div>
          ) : (
            <ChatContainer className="flex-1">
              <ChatMessages messages={messages}>
                <div className="p-4">
                  {hasOlder && <div ref={topSentinelRef} />}
                  <MessageList
                    messages={messages}
                    conversationId={conversationId}
                    isTyping={isGenerating}
                    isGenerating={isGenerating}
                    onRedo={(uuid) => {
                      // uuid is backendId; locate the assistant message by backendId
                      const idx = messages.findIndex(
                        (m: any) =>
                          (m as any).backendId === uuid || m.id === uuid
                      );
                      if (idx === -1) return;
                      const prevUser = [...messages]
                        .slice(0, idx)
                        .reverse()
                        .find((m: any) => m.role === "user");
                      if (!prevUser) return;

                      // remove old assistant message
                      updateMessages((prev) =>
                        prev.filter(
                          (m: any) =>
                            (m as any).backendId !== uuid && m.id !== uuid
                        )
                      );

                      // Resend prompt without duplicating user bubble
                      sendMessage(
                        prevUser.content,
                        projectId ?? null,
                        undefined,
                        {
                          addUserMessage: false,
                        }
                      );
                    }}
                    showTimeStamps={false}
                  />
                  <div ref={messagesEndRef} />
                </div>
              </ChatMessages>
            </ChatContainer>
          )}
        </div>

        {/* Error message */}
        {error && (
          <div className="px-4 py-2 bg-red-50 border-t border-red-200">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Input */}
        <div className="p-4 bg-white border-t">
          {/* File attachments at the top */}
          {files && files.length > 0 && (
            <div className="pb-2 mb-2 border-b border-gray-100">
              <div className="flex gap-2 overflow-x-auto">
                {files.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-1 text-sm text-gray-600 bg-gray-50 rounded-md px-2 py-1"
                  >
                    <span className="truncate max-w-[150px]">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setFiles(files.filter((_, i) => i !== index));
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <MessageInput
              value={input}
              onChange={handleInputChange}
              placeholder="Ask about your cloud migration..."
              isGenerating={isGenerating}
              allowAttachments={true}
              remainingQuota={
                quotaRemaining === Infinity ? undefined : quotaRemaining
              }
              files={files}
              setFiles={setFiles}
              className="min-h-[44px]"
            />
          </form>
        </div>
      </div>
    </>
  );
}
